import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import _get from 'lodash/get';
import classnames from 'classnames';
import ContactUsFormModal from './ContactUsFormModal';
import authDao from '../../../dao/authDao';
import smbUtils from '../../../utils/smbUtils';
import frontendUtils from '../../../utils/frontendUtils';
import subscriptionUtils from '../../../utils/subscriptionUtils';
import timeUtils from '../../../utils/timeUtils';
import {useDispatch, useSelector} from 'react-redux';
import {history} from '../../../utils/redux/store';
import {loadAuth} from '../../../components/auth/reducers';
import {multipleSubscriptionsUtils} from '../../../utils/solutionUtils';
import {privilegeUtils} from '../../../utils/privilegeUtils';

export default function SmbAuthModal({
  auth,
  showAuthModal = false,
  setShowAuthModal,
}) {
  const isSkincarePro =
    useSelector(
      state => state.components.auth.accountSummary.customer.functionType
    ) === 'SKINCARE_PRO';
  const {customerId} = smbUtils.customerInfo(auth);
  const noSubscription = subscriptionUtils.noSubscription(auth);
  const isEnterprise = subscriptionUtils.isEnterprise(auth);
  const isOwner = subscriptionUtils.isOwnerRole(auth);
  const overdueSolutions = multipleSubscriptionsUtils.overdueSolutions(auth);
  const isMultiOverdue = overdueSolutions.length > 1;
  const isSkinAnalysisOverdue = overdueSolutions.includes('SKIN_ANALYSIS');
  const isSubscriptionOverdueAreTrials = multipleSubscriptionsUtils.isSubscriptionOverdueAreTrials(
    auth
  );
  const isScpActivationAllowed =
    isSkincarePro && smbUtils.isScpActivationAllowed(auth);

  const [showForm, setShowForm] = useState(false);
  const [chooseFunctionType, setChooseFunctionType] = useState(false);
  const [accountServices, setAccountServices] = useState([]);
  const [filterServices, setFilterServices] = useState([]);
  const functionType = _get(auth, 'customer.functionType');
  const functionTypeI18n = smbUtils.getFunctionTypeI18n(functionType);
  const solutionInfos = multipleSubscriptionsUtils.getSolutionInfos(auth);
  const after180days = timeUtils.unixToShortTimeAfterDays(
    _get(auth.subscriptionItem, 'endTime', 0),
    180
  );
  const after180days4SkinAnalysis = timeUtils.unixToShortTimeAfterDays(
    _get(solutionInfos, 'SKIN_ANALYSIS.endTime', 0),
    180
  );
  const after180days4Simulation = timeUtils.unixToShortTimeAfterDays(
    _get(solutionInfos, 'AESTHETIC_SIMULATION.endTime', 0),
    180
  );

  const getPlanPriceUrl = () => {
    let planType;
    if (isMultiOverdue) {
      if (after180days4Simulation > after180days4SkinAnalysis) {
        planType = 'SKIN_ANALYSIS';
      } else {
        planType = 'AESTHETIC_SIMULATION';
      }
    } else {
      planType = overdueSolutions[0];
    }

    window.location.href = frontendUtils.getPlanPriceUrl(
      'subscribe-now',
      customerId,
      planType
    );
  };

  const dispatch = useDispatch();
  const {t} = useTranslation();

  useEffect(() => {
    if (privilegeUtils.isTechnicalSupport(auth)) {
      authDao.listTechnicalSupportCustomers().then(r => {
        setAccountServices(r.map(rr => rr.customer));
        setFilterServices(r.map(rr => rr.customer));
      });
    } else {
      authDao.listAccountService().then(r => {
        setAccountServices(r);
        setFilterServices(r);
      });
    }
  }, []);

  const switchCustomer = id => {
    authDao.switchAccountCustomer(id).then(r => {
      if (r && r.status && r.status === 'success') {
        dispatch(loadAuth());
        history.push('/');
      }
    });
  };

  const searchCustomer = param => {
    setFilterServices(
      accountServices.filter(
        s =>
          s.customerName.toUpperCase().includes(param.toUpperCase()) ||
          s.contactEmail.toUpperCase().includes(param.toUpperCase())
      )
    );
  };

  const getHeader = () => {
    switch (true) {
      case noSubscription:
        return (
          <Trans i18nKey="could.not.sign.in.title">Could Not Sign In</Trans>
        );
      case chooseFunctionType:
        return <Trans i18nKey="c.c.select.placeholder">Select...</Trans>;
      case isMultiOverdue:
        return (
          <Trans i18nKey="expired.multi.subscription.v3.title">
            Your{' '}
            {{
              plan: isEnterprise
                ? t('u.s.skincare.plan.services', 'Services')
                : isSubscriptionOverdueAreTrials
                ? t('u.s.skincare.plan.free.trials', 'Free Trials')
                : t('u.s.skincare.plan.subscriptions', 'Subscriptions'),
            }}{' '}
            have ended!
          </Trans>
        );
      case isSkincarePro && !isScpActivationAllowed:
        return (
          <Trans i18nKey="u.s.skincare.plan.not.ready.title">
            Account Setup in Progress
          </Trans>
        );
      default:
        return (
          <Trans i18nKey="expired.subscription.v3.title">
            Your{' '}
            {{
              plan: isEnterprise
                ? t('u.s.skincare.plan.service', 'Service')
                : isSubscriptionOverdueAreTrials
                ? t('u.s.skincare.plan.free.trial', 'Free Trial')
                : t('u.s.skincare.plan.subscription', 'Subscription'),
            }}{' '}
            has ended!
          </Trans>
        );
    }
  };

  const getDesc = () => {
    switch (true) {
      case noSubscription:
        return (
          <Trans i18nKey="could.not.sign.in.desc2">
            Login failed. Please check your username, password, and internet
            connection. You can try again later.
          </Trans>
        );
      case chooseFunctionType:
        return (
          <div>
            <div
              style={{
                left: 0,
                width: 'min-content',
              }}
            >
              <ul className="kt-nav kt-margin-t-10 kt-margin-b-10">
                <div
                  style={{
                    fontSize: '1.2rem',
                    padding: '0 1.75rem',
                    fontWeight: 500,
                    minWidth: 'max-content',
                  }}
                >
                  <input
                    type="text"
                    className="kt-margin-t-10 kt-margin-b-10 form-control"
                    onChange={e => {
                      searchCustomer(e.target.value);
                    }}
                    placeholder={t(
                      'c.l.h.select.account.search',
                      'Search by email or account name'
                    )}
                  />
                </div>
                {filterServices.map((c, i) => (
                  <li className="kt-nav__item" key={`functionType-option${i}`}>
                    <a
                      href="javascript:;"
                      onClick={() => switchCustomer(c.id)}
                      className="kt-nav__link"
                    >
                      <span className="kt-nav__link-icon kt-margin-r-10">
                        <div
                          className={classnames({
                            [`${c.functionType.toLowerCase()}-icon`]: true,
                            [`${c.functionType.toLowerCase()}-icon--active`]:
                              auth.customer.id === c.id,
                          })}
                        />
                      </span>{' '}
                      <span
                        className="kt-nav__link-text"
                        style={{
                          color: auth.customer.id === c.id && '#5867dd',
                        }}
                      >
                        <div
                          className="d-flex"
                          style={{flexDirection: 'column'}}
                        >
                          <div>
                            {smbUtils.getFunctionTypeI18n(c.functionType)}
                          </div>
                          <div className="font-weight-bold">
                            {c.customerName}
                          </div>
                          <div>{c.contactEmail}</div>
                        </div>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      case isSkincarePro && !isScpActivationAllowed:
        return (
          <div>
            <p>
              <Trans i18nKey="u.s.skincare.plan.not.ready.desc">
                Your account is still being set up. Please try again later and
                click the button below to retry.
              </Trans>
            </p>
            <div className="d-flex flex-column align-items-center">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  window.location.reload();
                }}
              >
                <div>
                  <Trans i18nKey="u.s.skincare.plan.not.ready.retry">
                    Try it now
                  </Trans>
                </div>
              </button>
              <br />
            </div>
            <div className="d-flex flex-column align-items-center">
              <p>
                <Trans i18nKey="expired.subscription.v2.desc3">
                  Need help? Please
                  <button
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#5d78ff',
                    }}
                    onClick={() => setShowForm(true)}
                  >
                    <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                  </button>
                  .
                </Trans>
              </p>
            </div>
          </div>
        );
      case isSubscriptionOverdueAreTrials:
        return (
          <div>
            <p>
              <Trans i18nKey="expired.multi.trial.v3.desc1">
                We hope you had a great time exploring how {{functionTypeI18n}}{' '}
                can help you offer the best user experience to your customers!
              </Trans>
            </p>
            <p>
              {isOwner ? (
                <Trans i18nKey="expired.multi.trial.v3.desc2">
                  Subscribe now to keep your access to {{functionTypeI18n}} and
                  enjoy all the incredible features.
                </Trans>
              ) : (
                <Trans i18nKey="expired.multi.trial.v3.desc2.1">
                  Contact the Account Owner now to keep your access to{' '}
                  {{functionTypeI18n}} and enjoy all the incredible features.
                </Trans>
              )}
            </p>
            {isMultiOverdue ? (
              <>
                <p>
                  <Trans i18nKey="expired.multi.trial.v3.desc3">
                    If no action is taken, your analytics data will be deleted
                    after 180 days on:
                  </Trans>
                </p>
                <div className="d-flex flex-column align-items-center">
                  <ui>
                    <li>
                      {t('u.s.skincare.skin.analysis', 'Skin Analysis')}:{' '}
                      <strong>{after180days4SkinAnalysis}</strong>
                    </li>
                    <li>
                      {t(
                        'u.s.skincare.aesthetic.simulation',
                        'Aesthetic Simulation'
                      )}
                      : <strong>{after180days4Simulation}</strong>
                    </li>
                  </ui>
                  <br />
                </div>
              </>
            ) : (
              <p>
                <Trans i18nKey="expired.trial.v3.desc3">
                  If no action is taken, your analytics data will be deleted
                  after 180 days on {{after180days}}.
                </Trans>
              </p>
            )}
            <div className="d-flex flex-column align-items-center">
              {isOwner && (
                <>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      getPlanPriceUrl();
                    }}
                  >
                    <div>
                      <Trans i18nKey="expired.trial.compare.subscribe">
                        Compare Plans and Subscribe
                      </Trans>
                    </div>
                  </button>
                  <br />
                </>
              )}
            </div>
            <p>
              <Trans i18nKey="expired.trial.v2.desc3">
                If you need our assistance on selecting a best plan, please
                <button
                  type="button"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#5d78ff',
                    textDecoration: 'underline',
                  }}
                  onClick={() => setShowForm(true)}
                >
                  <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                </button>
                .
              </Trans>
            </p>
          </div>
        );
      case isSkincarePro:
        if (isMultiOverdue) {
          return (
            <div>
              <p>
                <Trans i18nKey="expired.multi.subscription.v3.desc1">
                  We hope you had a great time using {{functionTypeI18n}}'s{' '}
                  <strong>
                    {{
                      plan1: t('u.s.skincare.skin.analysis', 'Skin Analysis'),
                    }}
                  </strong>{' '}
                  and{' '}
                  <strong>
                    {{
                      plan2: t(
                        'u.s.skincare.aesthetic.simulation',
                        'Aesthetic Simulation'
                      ),
                    }}
                  </strong>
                  !
                </Trans>
              </p>
              <p>
                <Trans i18nKey="expired.multi.subscription.v3.desc2">
                  Your{' '}
                  {{
                    plan: isEnterprise
                      ? t('u.s.skincare.plan.services.lower', 'services')
                      : t(
                          'u.s.skincare.plan.subscriptions.lower',
                          'subscriptions'
                        ),
                  }}{' '}
                  have now expired.&nbsp;
                </Trans>
                {!isOwner ? (
                  <Trans i18nKey="expired.multi.subscription.v3.desc3">
                    Please contact the Account Owner to extend the{' '}
                    {{
                      plan: isEnterprise
                        ? t('u.s.skincare.plan.service.lower', 'service')
                        : t(
                            'u.s.skincare.plan.subscription.lower',
                            'subscription'
                          ),
                    }}{' '}
                    plan, so that you can save all your settings and keep using
                    the platform.
                  </Trans>
                ) : (
                  <Trans i18nKey="expired.subscription.v3.desc3.2">
                    You save all your settings and keep using the platform by
                    clicking the button below to extend your{' '}
                    {{
                      plan: isEnterprise
                        ? t('u.s.skincare.plan.service.lower', 'service')
                        : t(
                            'u.s.skincare.plan.subscription.lower',
                            'subscription'
                          ),
                    }}
                    .
                  </Trans>
                )}
              </p>
              <p>
                <Trans i18nKey="expired.multi.trial.v3.desc3">
                  If no action is taken, your analytics data will be deleted
                  after 180 days on:
                </Trans>
              </p>
              <div className="d-flex flex-column align-items-center">
                <ui>
                  <li>
                    {t('u.s.skincare.skin.analysis', 'Skin Analysis')}:{' '}
                    <strong>{after180days4SkinAnalysis}</strong>
                  </li>
                  <li>
                    {t(
                      'u.s.skincare.aesthetic.simulation',
                      'Aesthetic Simulation'
                    )}
                    : <strong>{after180days4Simulation}</strong>
                  </li>
                </ui>
                <br />
                {isOwner && (
                  <>
                    {isEnterprise ? (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                          window.location.href = frontendUtils.getContactUsUrl(
                            'app-auth',
                            'sales'
                          );
                        }}
                      >
                        <div>
                          <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                        </div>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                          getPlanPriceUrl();
                        }}
                      >
                        <div>
                          <Trans i18nKey="expired.subscription.extend">
                            Extend My subscription
                          </Trans>
                        </div>
                      </button>
                    )}
                    <br />
                  </>
                )}
                <p>
                  <Trans i18nKey="expired.subscription.v2.desc3">
                    Need help? Please
                    <button
                      type="button"
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#5d78ff',
                        textDecoration: 'underline',
                      }}
                      onClick={() => setShowForm(true)}
                    >
                      <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                    </button>
                    .
                  </Trans>
                </p>
              </div>
            </div>
          );
        }
        return (
          <div>
            <p>
              <Trans i18nKey="expired.subscription.v3.desc1">
                We hope you had a great time using {{functionTypeI18n}}'s{' '}
                <strong>
                  {{
                    plan1: isSkinAnalysisOverdue
                      ? t('u.s.skincare.skin.analysis', 'Skin Analysis')
                      : t(
                          'u.s.skincare.aesthetic.simulation',
                          'Aesthetic Simulation'
                        ),
                  }}
                </strong>
                !
              </Trans>
            </p>
            <p>
              <Trans i18nKey="expired.subscription.v3.desc2">
                Your{' '}
                {{
                  plan: isEnterprise
                    ? t('u.s.skincare.plan.service.lower', 'service')
                    : t('u.s.skincare.plan.subscription.lower', 'subscription'),
                }}{' '}
                has now expired.&nbsp;
              </Trans>
              {!isOwner ? (
                <Trans i18nKey="expired.multi.Subscription.v3.desc3">
                  Please contact the Account Owner to extend the{' '}
                  {{
                    plan: isEnterprise
                      ? t('u.s.skincare.plan.service.lower', 'service')
                      : t(
                          'u.s.skincare.plan.subscription.lower',
                          'subscription'
                        ),
                  }}{' '}
                  plan, so that you can save all your settings and keep using
                  the platform.
                </Trans>
              ) : (
                <Trans i18nKey="expired.subscription.v3.desc3.2">
                  You save all your settings and keep using the platform by
                  clicking the button below to extend your{' '}
                  {{
                    plan: isEnterprise
                      ? t('u.s.skincare.plan.service.lower', 'service')
                      : t(
                          'u.s.skincare.plan.subscription.lower',
                          'subscription'
                        ),
                  }}
                  .
                </Trans>
              )}
            </p>
            <p>
              <Trans i18nKey="expired.trial.v3.desc3">
                If no action is taken, your analytics data will be deleted after
                180 days on {{after180days}}.
              </Trans>
            </p>
            <div className="d-flex flex-column align-items-center">
              {isOwner && (
                <>
                  {isEnterprise ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        window.location.href = frontendUtils.getContactUsUrl(
                          'app-auth',
                          'sales'
                        );
                      }}
                    >
                      <div>
                        <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        getPlanPriceUrl();
                      }}
                    >
                      <div>
                        <Trans i18nKey="expired.subscription.extend">
                          Extend My subscription
                        </Trans>
                      </div>
                    </button>
                  )}
                  <br />
                </>
              )}
              <p>
                <Trans i18nKey="expired.subscription.v2.desc3">
                  Need help? Please
                  <button
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#5d78ff',
                      textDecoration: 'underline',
                    }}
                    onClick={() => setShowForm(true)}
                  >
                    <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                  </button>
                  .
                </Trans>
              </p>
            </div>
          </div>
        );
      default:
        return (
          <div>
            <p>
              <Trans i18nKey="expired.single.subscription.v3.desc1">
                We hope you had a great time using Online Service{' '}
                {{functionTypeI18n}}!
              </Trans>
            </p>
            <p>
              <Trans i18nKey="expired.subscription.v3.desc2">
                Your{' '}
                {{
                  plan: t(
                    'u.s.skincare.plan.subscriptions.lower',
                    'subscription'
                  ),
                }}{' '}
                has now expired.&nbsp;
              </Trans>
              {!isOwner ? (
                <Trans i18nKey="expired.multi.Subscription.v3.desc3">
                  Please contact the Account Owner to extend the{' '}
                  {{
                    plan: t(
                      'u.s.skincare.plan.subscription.lower',
                      'subscription'
                    ),
                  }}{' '}
                  plan, so that you can save all your settings and keep using
                  the platform.
                </Trans>
              ) : (
                <Trans i18nKey="expired.subscription.v3.desc3.2">
                  You save all your settings and keep using the platform by
                  clicking the button below to extend your{' '}
                  {{
                    plan: t(
                      'u.s.skincare.plan.subscriptions.lower',
                      'subscriptions'
                    ),
                  }}
                  .
                </Trans>
              )}
            </p>
            <p>
              <Trans i18nKey="expired.trial.v3.desc3">
                If no action is taken, your analytics data will be deleted after
                180 days on {{after180days}}.
              </Trans>
            </p>
            <div className="d-flex flex-column align-items-center">
              {isOwner && (
                <>
                  {isEnterprise ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        window.location.href = frontendUtils.getContactUsUrl(
                          'app-auth',
                          'sales'
                        );
                      }}
                    >
                      <div>
                        <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        getPlanPriceUrl();
                      }}
                    >
                      <div>
                        <Trans i18nKey="expired.subscription.extend">
                          Extend My subscription
                        </Trans>
                      </div>
                    </button>
                  )}
                  <br />
                </>
              )}
              <p>
                <Trans i18nKey="expired.subscription.v2.desc3">
                  Need help? Please
                  <button
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#5d78ff',
                      textDecoration: 'underline',
                    }}
                    onClick={() => setShowForm(true)}
                  >
                    <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                  </button>
                  .
                </Trans>
              </p>
            </div>
          </div>
        );
    }
  };

  const getFooter = () => {
    switch (true) {
      case noSubscription:
        return (
          <div className="d-flex flex-column align-items-center">
            <p>
              <Trans i18nKey="expired.subscription.v2.desc3">
                Need help? Please
                <button
                  type="button"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#5d78ff',
                    textDecoration: 'underline',
                  }}
                  onClick={() => setShowForm(true)}
                >
                  <Trans i18nKey="p.contactus.title">Contact Us</Trans>
                </button>
                .
              </Trans>
            </p>
          </div>
        );
      case chooseFunctionType:
        return (
          <button
            type="button"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#5d78ff',
              textDecoration: 'underline',
            }}
            onClick={() => setChooseFunctionType(false)}
          >
            <Trans i18nKey="p.s.recommendation.rule.back">Back</Trans>
          </button>
        );
      default:
        return (
          <Trans i18nKey="expired.trial.v2.footer">
            Using multiple services on Online Service?{' '}
            <button
              type="button"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#5d78ff',
                textDecoration: 'underline',
              }}
              onClick={() => setChooseFunctionType(true)}
            >
              Click here
            </button>{' '}
            to switch to another service.
          </Trans>
        );
    }
  };

  return (
    <>
      <Modal
        size={chooseFunctionType ? 'md' : 'lg'}
        show={showAuthModal}
        centered={true}
        onHide={() => {}}
      >
        <Modal.Body className="d-flex flex-column align-items-center kt-padding-25">
          {functionType === 'SKINCARE_PRO' &&
            isOwner &&
            isScpActivationAllowed && (
              <button
                type="button"
                className="btn"
                style={{position: 'absolute', right: 0, top: '10px'}}
                onClick={() => setShowAuthModal(false)}
              >
                <i className="fa fa-times" />
              </button>
            )}
          {!chooseFunctionType && (
            <img
              alt="logo"
              src={require('../../../assets/images/home/icon _timer.svg')}
              style={{mixWidth: '44px', padding: '5px'}}
            />
          )}
          <h2 style={{fontWeight: 'bold', padding: '1rem 0'}}>{getHeader()}</h2>
          <div className="d-flex flex-column align-items-center">
            {getDesc()}
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <div>{getFooter()}</div>
        </Modal.Footer>
      </Modal>
      <ContactUsFormModal showForm={showForm} setShowForm={setShowForm} />
    </>
  );
}
